import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { formatApiErrorDetail } from '../../auth';

export const useUserManagement = (userId, onUpdated) => {
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`;
  const [data, setData] = useState(null), [loading, setLoading] = useState(true), [busy, setBusy] = useState(false);
  const [error, setError] = useState(''), [notice, setNotice] = useState(''), [proposal, setProposal] = useState(null);
  const active = useRef(null), mutating = useRef(false), sequence = useRef(0), updated = useRef(onUpdated);
  updated.current = onUpdated;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('gv_token')}` });
  const accept = useCallback((value) => { setData(value); updated.current?.(value); }, []);
  const load = useCallback(async () => {
    if (mutating.current || !active.current || active.current.signal.aborted) return;
    const controller = active.current, request = ++sequence.current;
    try {
      const { data } = await axios.get(`${base}/details`, { headers: headers(), signal: controller.signal });
      if (!controller.signal.aborted && sequence.current === request) accept(data);
    } catch (failure) { if (!axios.isCancel(failure) && sequence.current === request) { setError(formatApiErrorDetail(failure.response?.data?.detail || 'Unable to load this account.')); if ([401, 403].includes(failure.response?.status)) setData(null); } }
    finally { if (!controller.signal.aborted && sequence.current === request) setLoading(false); }
  }, [base, accept]);
  useEffect(() => {
    active.current = new AbortController(); load();
    const poll = setInterval(load, 15000);
    window.addEventListener('focus', load); window.addEventListener('gv-wallet-updated', load);
    return () => { active.current.abort(); clearInterval(poll); window.removeEventListener('focus', load); window.removeEventListener('gv-wallet-updated', load); };
  }, [load]);
  const prepare = (kind, extra = {}) => { setError(''); setNotice(''); setProposal({ kind, payload: { request_id: crypto.randomUUID(), controls_revision: data.controls_revision, confirmed: true, ...extra } }); };
  const execute = async () => {
    if (!proposal || mutating.current) return;
    mutating.current = true; sequence.current += 1; setBusy(true); setError('');
    const controller = active.current;
    const routes = { timeout: 'originals-timeout', lift: 'originals-timeout/clear', reset: 'daily-credits/reset' };
    try {
      const { data } = await axios.post(`${base}/${routes[proposal.kind]}`, proposal.payload, { headers: headers(), signal: controller.signal });
      if (!controller.signal.aborted) { accept(data); setNotice({ timeout: 'Originals timeout applied.', lift: 'Originals timeout removed.', reset: 'Daily claim is eligible again. No credits were added.' }[proposal.kind]); setProposal(null); }
    } catch (failure) {
      if (!axios.isCancel(failure) && !controller.signal.aborted) {
        setError(formatApiErrorDetail(failure.response?.data?.detail || 'Confirmation delayed. Retry the same action.'));
        if (failure.response?.status === 409) { setProposal(null); mutating.current = false; await load(); }
      }
    } finally { if (!controller.signal.aborted) { mutating.current = false; setBusy(false); } }
  };
  return { data, loading, busy, error, notice, proposal, prepare, execute, cancel: () => setProposal(null), reload: load };
};